import { Slot, Slottable } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import React, {
  cloneElement,
  forwardRef,
  type ForwardRefExoticComponent,
  type ReactElement,
  type RefAttributes,
} from 'react'

import type { IconPropsType } from '@/legacy-src/components/icons/Icon'
import type Icon from '@/legacy-src/components/icons/Icon'
import { cn } from '@/legacy-src/lib/utils'

import { Text, type TextProps } from '@/src/domains/design-system/components/Text'

import { ButtonSize, ButtonVariant } from './Button.constants'

// the commented variants are kept here for reference
export const buttonVariants = cva(
  'inline-flex appearance-none items-center justify-center border-2 border-solid border-transparent font-sans uppercase no-underline ring-offset-background transition-colors hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:border-sand-400 disabled:bg-sand-400 disabled:!text-sand-700',
  {
    variants: {
      variant: {
        [`${ButtonVariant.PRIMARY}`]:
          'border-primary bg-primary text-white hover:border-peach-600 hover:bg-peach-600 active:border-peach-400 active:bg-peach-400',
        [`${ButtonVariant.SECONDARY}`]:
          'border-sand-400 bg-sand-400 text-secondary hover:border-sand-600 hover:bg-sand-600 active:border-sand-700 active:bg-sand-700',
        [`${ButtonVariant.ALTERNATIVE}`]:
          'border-petrol-400 bg-transparent text-white hover:bg-petrol-400 active:border-white active:bg-transparent',
        [`${ButtonVariant.YELLOW}`]:
          'border-yellow-500 bg-yellow-500 text-petrol-500 hover:border-yellow-600 hover:bg-yellow-600 active:border-yellow-400 active:bg-yellow-400',
        [`${ButtonVariant.OUTLINE}`]:
          'border-petrol-500 bg-transparent text-petrol-500 hover:border-petrol-300 hover:bg-petrol-300 hover:text-white active:border-petrol-500 active:bg-petrol-500 active:text-white',
        [`${ButtonVariant.PETROL}`]:
          'border-petrol-500 bg-petrol-500 text-white hover:border-petrol-600 hover:bg-petrol-600 active:border-petrol-400 active:bg-petrol-400',
      },
      size: {
        [`${ButtonSize.BIG}`]: 'min-h-[4.25rem] rounded-2xl p-6',
        [`${ButtonSize.MEDIUM}`]: 'min-h-[3.375rem] rounded-xl px-5 py-3',
        [`${ButtonSize.SMALL}`]: 'min-h-[2.75rem] rounded-xl p-3',
        [`${ButtonSize.TEXT}`]: 'normal-case',
      },
    },
    compoundVariants: [
      {
        variant: ButtonVariant.PRIMARY,
        size: ButtonSize.TEXT,
        className:
          'border-none bg-transparent p-0 text-primary hover:bg-transparent hover:text-peach-600 active:bg-transparent disabled:border-transparent disabled:bg-transparent',
      },
    ],
    defaultVariants: {
      variant: ButtonVariant.PRIMARY,
    },
  },
)

interface ButtonVariantProps extends VariantProps<typeof buttonVariants> {}

export interface ButtonSharedProps extends ButtonVariantProps {
  size: NonNullable<ButtonVariantProps['size']>
  prefixIcon?: ReactElement<IconPropsType, typeof Icon>
  suffixIcon?: ReactElement<IconPropsType, typeof Icon>
}

export interface ButtonAsChildProps extends ButtonSharedProps, Omit<React.HTMLAttributes<HTMLElement>, 'children'> {
  asChild: true
  children: ReactElement
}
export interface ButtonAsChildWithRefProps extends ButtonAsChildProps, RefAttributes<HTMLElement> {}

export interface ButtonElementProps extends ButtonSharedProps, React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: false
}
export interface ButtonElementWithRefProps extends ButtonElementProps, RefAttributes<HTMLButtonElement> {}

export type ButtonProps = ButtonAsChildProps | ButtonElementProps
export type ButtonWithRefProps = ButtonAsChildWithRefProps | ButtonElementWithRefProps

const textVariantMap: Record<NonNullable<ButtonProps['size']>, TextProps['variant']> = {
  small: 'button-s',
  medium: 'button-m',
  big: 'button-l',
  text: 'base',
}

const textWeightMap: Record<NonNullable<ButtonProps['size']>, TextProps['weight']> = {
  small: 'bold',
  medium: 'bold',
  big: 'bold',
  text: 'semibold',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, children, prefixIcon, suffixIcon, ...props }, ref) => {
    const Comp = props.asChild ? Slot : 'button'

    const defaultProps = props.asChild ? {} : { type: 'button' as const }

    const { asChild, ...restProps } = props

    return (
      <Text variant={textVariantMap[size]} weight={textWeightMap[size]} asChild className={className}>
        <Comp className={cn(buttonVariants({ variant, size }))} ref={ref} {...defaultProps} {...restProps}>
          {prefixIcon
            ? cloneElement(prefixIcon, {
                size: size === 'small' ? 16 : 24,
                className: cn('mr-1', prefixIcon.props.className),
              })
            : null}
          <Slottable>{children}</Slottable>
          {suffixIcon
            ? cloneElement(suffixIcon, {
                size: size === 'small' ? 16 : 24,
                className: cn('ml-1', suffixIcon.props.className),
              })
            : null}
        </Comp>
      </Text>
    )
  },
) as ForwardRefExoticComponent<ButtonWithRefProps>

Button.displayName = 'Button'
